import { cn, useDebounceState } from "@theprelude/core";
import { ButtonHTMLAttributes, ForwardedRef, forwardRef, useMemo } from "react";
import { ControllerRenderProps, FieldPath, FieldValues } from "react-hook-form";
import { Button } from "../button/button";
import { IconButton } from "../button/icon-button";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownLabel,
  DropdownTrigger,
} from "../dropdown/dropdown";
import { Input } from "../forms/input";
import { CloseIcon } from "../icons/close-icon";
import { RotatingChevron } from "../icons/rotating-chevron";
import { SearchIcon } from "../icons/search-icon";
import { usePopover } from "../popover/popover";
import {
  ScrollArea,
  ScrollAreaViewport,
  ScrollBar,
} from "../scroll-area/scroll-area";
import { Truncate } from "../truncate/truncate";
import { FormField, TFormField } from "./form-field";

interface DropdownFormFieldProps<
  TValue = string,
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends TFormField<TFieldValues, TFieldPath>,
    Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      "name" | "size" | "children" | "onClick"
    > {
  options: DropdownFormOption<TValue>[];
  readOnly?: boolean;
  placeholder?: string;
}

export interface DropdownFormOption<TValue> {
  label: string;
  value: TValue;
  disabled?: boolean;
  group?: string;
}

export const DropdownFormField = forwardRef(function DropdownFormField<
  TValue = string,
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  {
    name,
    options,
    disabled,
    readOnly,
    contentClassName,
    "data-testid": dataTestId,
    ...props
  }: DropdownFormFieldProps<TValue, TFieldValues, TFieldPath> & {
    contentClassName?: string;
    "data-testid"?: string;
  },
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const dropdown = usePopover();
  const disableTrigger = disabled || readOnly;

  const placeholderText = props.placeholder ?? "Select an option";

  return (
    <FormField name={name} {...props}>
      {(field) => {
        const selectedLabel = options.find(
          (o) => o.value === field.value,
        )?.label;
        return (
          <Dropdown {...dropdown}>
            <DropdownTrigger asChild disabled={disableTrigger}>
              <Button
                ref={ref}
                intent="secondary"
                className="w-full justify-between font-normal"
                disabled={disableTrigger}
                icon={<RotatingChevron active={dropdown.open} />}
                iconPosition="right"
                data-testid={dataTestId}
              >
                <div className="truncate">
                  {selectedLabel || placeholderText}
                </div>
              </Button>
            </DropdownTrigger>
            <DropdownContent
              align="start"
              className={cn("max-w-[800px] p-0", contentClassName)}
            >
              <Content<TValue, TFieldValues, TFieldPath>
                field={field}
                options={options}
              />
            </DropdownContent>
          </Dropdown>
        );
      }}
    </FormField>
  );
});
interface ContentProps<
  TValue,
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues>,
> {
  field: Omit<ControllerRenderProps<TFieldValues, TFieldPath>, "ref"> & {
    id: string;
    required?: boolean;
    className?: string;
  };
  options: DropdownFormOption<TValue>[];
}

function Content<
  TValue,
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues>,
>({ field, options }: ContentProps<TValue, TFieldValues, TFieldPath>) {
  const searchTerm = useDebounceState<string>("", 500);

  const groupedOptions = useMemo(() => {
    const lowerSearch = searchTerm.debouncedValue.toLowerCase();
    const filteredOptions = options.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearch),
    );

    const grouped: Record<string, DropdownFormOption<TValue>[]> = {};

    filteredOptions.forEach((opt) => {
      const groupName = opt.group || "";
      if (!grouped[groupName]) {
        grouped[groupName] = [];
      }
      grouped[groupName]?.push(opt);
    });

    return grouped;
  }, [options, searchTerm.debouncedValue]);

  const canSearch = options.length > 8;
  return (
    <>
      {canSearch && (
        <Input
          wrapperClassName="mx-3 mt-2"
          value={searchTerm.value ?? ""}
          onChange={(e) => searchTerm.setDelayed(e.target.value)}
          onKeyDown={(ev) => {
            if (ev.key !== "Escape") {
              ev.stopPropagation();
            }
          }}
          before={<SearchIcon className="text-secondary-foreground" />}
          after={
            <IconButton
              icon={<CloseIcon />}
              onClick={() => {
                searchTerm.setImmediate("");
              }}
            />
          }
          placeholder="Search..."
          autoFocus
        />
      )}
      <ScrollArea
        className={cn(
          "mt-2 h-full w-full min-w-[200px]",
          canSearch && "h-[55vh] px-1 pb-3",
        )}
      >
        <ScrollAreaViewport>
          {Object.keys(groupedOptions).length > 0 ? (
            <div className="flex gap-2">
              {Object.entries(groupedOptions).map(
                ([groupName, groupOptions]) => (
                  <div key={groupName} className="flex w-full flex-col">
                    <DropdownLabel className="mb-2 px-2">
                      {groupName || "Select an option"}
                    </DropdownLabel>
                    <div className="flex w-full flex-col gap-1">
                      {groupOptions.map((option) => {
                        const isSelected = option.value === field.value;

                        return (
                          <DropdownItem
                            key={`${groupName}-${String(option.value)}-${option.label}`}
                            className={cn(
                              "min-w-0",
                              isSelected && "bg-secondary-background",
                            )}
                            onSelect={() => {
                              field.onChange(option.value);
                            }}
                            disabled={option.disabled}
                          >
                            <div className="min-w-0">
                              <Truncate text={option.label}>
                                {option.label}
                              </Truncate>
                            </div>
                          </DropdownItem>
                        );
                      })}
                    </div>
                  </div>
                ),
              )}
            </div>
          ) : (
            <span className="flex justify-center py-2 text-secondary-foreground text-xs">
              No results found
            </span>
          )}
        </ScrollAreaViewport>
        <ScrollBar orientation="vertical" />
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </>
  );
}
